import type {
  Document,
  DocumentMetadata,
  DocumentFormat,
  DocumentSource,
  RAGEnv,
  AutoIndexingConfig,
  R2Bucket,
  R2Object,
} from '../types';
import { DocumentProcessingError } from '../types';
import { DocumentLoader } from './document-loader';
import { generateId } from '../core/utils';

/**
 * Enhanced document processor with automatic format detection and processing
 * Supports zero-setup document ingestion from multiple sources
 */
export class AutoDocumentProcessor {
  private env: RAGEnv;
  private loader: DocumentLoader;
  private autoIndexingConfig: AutoIndexingConfig;

  constructor(env: RAGEnv, autoIndexingConfig: AutoIndexingConfig) {
    this.env = env;
    this.loader = new DocumentLoader(env);
    this.autoIndexingConfig = autoIndexingConfig;
  }

  /**
   * Automatically detect and process documents from various sources
   */
  async processAutoSources(): Promise<Document[]> {
    const documents: Document[] = [];

    for (const source of this.autoIndexingConfig.sources) {
      try {
        const sourceDocs = await this.processSource(source);
        documents.push(...sourceDocs);
      } catch (error) {
        console.warn(`Failed to process source ${source}:`, error);
      }
    }

    return documents;
  }

  /**
   * Process documents from a specific source
   */
  private async processSource(source: DocumentSource): Promise<Document[]> {
    switch (source) {
      case 'r2':
        return this.processR2Bucket();
      case 'github':
        return this.processGitHubRepository();
      case 'webhook':
        return this.processWebhookQueue();
      case 'email':
        return this.processEmailQueue();
      case 'auto':
        return this.processAutoDetectedSources();
      default:
        return [];
    }
  }

  /**
   * Process documents from R2 bucket
   */
  private async processR2Bucket(): Promise<Document[]> {
    const documents: Document[] = [];
    
    try {
      // List objects in the RAG bucket
      const objects = await this.env.RAG_BUCKET.list({
        limit: 1000,
      });

      for (const object of objects.objects) {
        if (this.shouldProcessFile(object.key)) {
          try {
            const document = await this.processR2Object(object);
            if (document) {
              documents.push(document);
            }
          } catch (error) {
            console.warn(`Failed to process R2 object ${object.key}:`, error);
          }
        }
      }
    } catch (error) {
      throw new DocumentProcessingError('r2-bucket', `R2 bucket processing failed: ${error}`);
    }

    return documents;
  }

  /**
   * Process a single R2 object
   */
  private async processR2Object(object: R2Object): Promise<Document | null> {
    try {
      const format = this.detectFormat(object.key);
      if (!this.autoIndexingConfig.supportedFormats.includes(format)) {
        return null;
      }

      // Get object content
      const objectData = await this.env.RAG_BUCKET.get(object.key);
      if (!objectData) return null;

      let content: string;
      const buffer = await objectData.arrayBuffer();

      // Process based on format
      switch (format) {
        case 'pdf':
          content = await this.processPDF(buffer);
          break;
        case 'docx':
          content = await this.processDocx(buffer);
          break;
        case 'image':
          content = await this.processImage(buffer, object.key);
          break;
        case 'audio':
          content = await this.processAudio(buffer, object.key);
          break;
        case 'video':
          content = await this.processVideo(buffer, object.key);
          break;
        default:
          content = await this.processText(buffer);
      }

      if (!content.trim()) {
        return null;
      }

      // Create document metadata
      const metadata: DocumentMetadata = {
        id: generateId(),
        title: this.extractTitle(object.key, content),
        format,
        source: 'r2',
        filePath: object.key,
        fileSize: buffer.byteLength,
        createdAt: object.uploaded?.getTime(),
        updatedAt: object.uploaded?.getTime(),
        custom: {
          r2Key: object.key,
          contentType: object.httpMetadata?.contentType,
          etag: object.etag,
        },
      };

      return {
        id: metadata.id,
        content,
        metadata,
      };
    } catch (error) {
      throw new DocumentProcessingError(object.key, `R2 object processing failed: ${error}`);
    }
  }

  /**
   * Process GitHub repositories
   */
  private async processGitHubRepository(): Promise<Document[]> {
    // This would integrate with GitHub API to fetch repository contents
    // For now, return empty array
    console.warn('GitHub repository processing not implemented yet');
    return [];
  }

  /**
   * Process webhook queue
   */
  private async processWebhookQueue(): Promise<Document[]> {
    if (!this.env.RAG_QUEUE) {
      return [];
    }

    // Process queued webhook messages
    // This would be implemented with Cloudflare Queues
    console.warn('Webhook queue processing not implemented yet');
    return [];
  }

  /**
   * Process email queue
   */
  private async processEmailQueue(): Promise<Document[]> {
    // This would integrate with email services to process attachments and content
    console.warn('Email queue processing not implemented yet');
    return [];
  }

  /**
   * Process auto-detected sources
   */
  private async processAutoDetectedSources(): Promise<Document[]> {
    // This would automatically detect and process various sources
    // Could include watching file system changes, API integrations, etc.
    console.warn('Auto-detected source processing not implemented yet');
    return [];
  }

  /**
   * Check if file should be processed based on patterns
   */
  private shouldProcessFile(filename: string): boolean {
    // Check file size limit
    // Note: R2 doesn't provide size in list, so we'll check during processing
    
    // Check exclude patterns
    for (const pattern of this.autoIndexingConfig.excludePatterns) {
      if (this.matchPattern(filename, pattern)) {
        return false;
      }
    }

    // Check include patterns
    for (const pattern of this.autoIndexingConfig.patterns) {
      if (this.matchPattern(filename, pattern)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Match filename against glob-like pattern
   */
  private matchPattern(filename: string, pattern: string): boolean {
    // Simple glob pattern matching
    const regex = pattern
      .replace(/\*\*/g, '.*')
      .replace(/\*/g, '[^/]*')
      .replace(/\?/g, '[^/]');
    
    return new RegExp(`^${regex}$`).test(filename);
  }

  /**
   * Detect document format from filename
   */
  private detectFormat(filename: string): DocumentFormat {
    const ext = filename.split('.').pop()?.toLowerCase();
    
    const formatMap: Record<string, DocumentFormat> = {
      'md': 'markdown',
      'markdown': 'markdown',
      'html': 'html',
      'htm': 'html',
      'pdf': 'pdf',
      'txt': 'txt',
      'docx': 'docx',
      'doc': 'docx',
      'csv': 'csv',
      'json': 'json',
      'xml': 'xml',
      'png': 'image',
      'jpg': 'image',
      'jpeg': 'image',
      'gif': 'image',
      'bmp': 'image',
      'webp': 'image',
      'mp3': 'audio',
      'wav': 'audio',
      'flac': 'audio',
      'mp4': 'video',
      'avi': 'video',
      'mkv': 'video',
      'webm': 'video',
    };

    return formatMap[ext || ''] || 'txt';
  }

  /**
   * Process PDF files using Cloudflare Workers AI
   */
  private async processPDF(buffer: ArrayBuffer): Promise<string> {
    try {
      if (this.env.AI) {
        // Use Cloudflare Workers AI for PDF text extraction
        const response = await this.env.AI.run('@cf/meta/llama-2-7b-chat-fp16', {
          prompt: 'Extract and summarize the text content from this PDF document',
          // Note: This is a placeholder - actual PDF processing would need OCR capabilities
        });
        
        if (response && typeof response === 'string') {
          return response;
        }
      }

      // Fallback - PDF processing requires specialized library
      console.warn('PDF processing requires Cloudflare AI or external library');
      return '';
    } catch (error) {
      console.warn('PDF processing failed:', error);
      return '';
    }
  }

  /**
   * Process DOCX files
   */
  private async processDocx(buffer: ArrayBuffer): Promise<string> {
    try {
      // Use existing docx processing from document loader
      const mammoth = await import('mammoth');
      const result = await mammoth.extractRawText({ arrayBuffer: buffer });
      return result.value;
    } catch (error) {
      console.warn('DOCX processing failed:', error);
      return '';
    }
  }

  /**
   * Process image files using Cloudflare Workers AI OCR
   */
  private async processImage(buffer: ArrayBuffer, filename: string): Promise<string> {
    try {
      if (this.env.AI) {
        // Use Cloudflare Workers AI for image OCR and description
        const response = await this.env.AI.run('@cf/microsoft/resnet-50', {
          image: Array.from(new Uint8Array(buffer)),
        });
        
        if (response && response.description) {
          return `Image: ${filename}\nDescription: ${response.description}`;
        }
      }

      // Fallback - basic image metadata
      return `Image file: ${filename}\nFormat: ${this.detectFormat(filename)}\nSize: ${buffer.byteLength} bytes`;
    } catch (error) {
      console.warn('Image processing failed:', error);
      return `Image file: ${filename}`;
    }
  }

  /**
   * Process audio files using Cloudflare Workers AI
   */
  private async processAudio(buffer: ArrayBuffer, filename: string): Promise<string> {
    try {
      if (this.env.AI) {
        // Use Cloudflare Workers AI for audio transcription
        const response = await this.env.AI.run('@cf/openai/whisper', {
          audio: Array.from(new Uint8Array(buffer)),
        });
        
        if (response && response.text) {
          return `Audio transcript: ${filename}\n${response.text}`;
        }
      }

      // Fallback - basic audio metadata
      return `Audio file: ${filename}\nFormat: ${this.detectFormat(filename)}\nSize: ${buffer.byteLength} bytes`;
    } catch (error) {
      console.warn('Audio processing failed:', error);
      return `Audio file: ${filename}`;
    }
  }

  /**
   * Process video files using Cloudflare Workers AI
   */
  private async processVideo(buffer: ArrayBuffer, filename: string): Promise<string> {
    try {
      if (this.env.AI) {
        // Use Cloudflare Workers AI for video analysis
        // This would extract keyframes and generate descriptions
        // Actual implementation would depend on available models
        return `Video file: ${filename}\nProcessed with Cloudflare AI`;
      }

      // Fallback - basic video metadata
      return `Video file: ${filename}\nFormat: ${this.detectFormat(filename)}\nSize: ${buffer.byteLength} bytes`;
    } catch (error) {
      console.warn('Video processing failed:', error);
      return `Video file: ${filename}`;
    }
  }

  /**
   * Process text-based files
   */
  private async processText(buffer: ArrayBuffer): Promise<string> {
    try {
      const decoder = new TextDecoder('utf-8');
      return decoder.decode(buffer);
    } catch (error) {
      // Try with different encodings
      try {
        const decoder = new TextDecoder('latin1');
        return decoder.decode(buffer);
      } catch (fallbackError) {
        console.warn('Text decoding failed:', fallbackError);
        return '';
      }
    }
  }

  /**
   * Extract title from filename and content
   */
  private extractTitle(filename: string, content: string): string {
    // Try to extract title from content first
    const lines = content.split('\n').slice(0, 10);
    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed.length > 0 && trimmed.length < 100) {
        // Check if it looks like a title
        if (/^#\s+(.+)/.test(trimmed)) {
          return trimmed.replace(/^#\s+/, '');
        }
        if (/^[A-Z]/.test(trimmed) && !trimmed.endsWith('.')) {
          return trimmed;
        }
      }
    }

    // Fallback to filename
    return filename.split('/').pop()?.replace(/\.[^.]+$/, '') || filename;
  }

  /**
   * Set up continuous monitoring for auto-indexing
   */
  async setupContinuousMonitoring(): Promise<void> {
    if (!this.autoIndexingConfig.enabled) {
      return;
    }

    // Set up R2 bucket notifications if possible
    // This would require Cloudflare Workers with R2 event triggers
    console.log('Continuous monitoring setup - would configure R2 event triggers');

    // Set up scheduled processing if cron is configured
    if (this.autoIndexingConfig.scheduleCron) {
      console.log(`Scheduled processing configured: ${this.autoIndexingConfig.scheduleCron}`);
      // This would be handled by Cloudflare Cron Triggers
    }
  }

  /**
   * Process webhook payload for document updates
   */
  async processWebhookPayload(payload: any, signature?: string): Promise<Document[]> {
    // Verify webhook signature if secret is configured
    if (this.autoIndexingConfig.webhookSecret && signature) {
      const isValid = await this.verifyWebhookSignature(payload, signature);
      if (!isValid) {
        throw new Error('Invalid webhook signature');
      }
    }

    // Process webhook payload based on source
    const documents: Document[] = [];

    if (payload.type === 'file_upload' && payload.files) {
      for (const file of payload.files) {
        try {
          const document = await this.processWebhookFile(file);
          if (document) {
            documents.push(document);
          }
        } catch (error) {
          console.warn(`Failed to process webhook file:`, error);
        }
      }
    }

    return documents;
  }

  /**
   * Verify webhook signature
   */
  private async verifyWebhookSignature(payload: any, signature: string): Promise<boolean> {
    try {
      const secret = this.autoIndexingConfig.webhookSecret!;
      const payloadString = JSON.stringify(payload);
      const encoder = new TextEncoder();
      const data = encoder.encode(payloadString);
      const key = await crypto.subtle.importKey(
        'raw',
        encoder.encode(secret),
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['sign']
      );
      const hashBuffer = await crypto.subtle.sign('HMAC', key, data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      const expectedSignature = `sha256=${hashHex}`;
      
      return expectedSignature === signature;
    } catch (error) {
      console.warn('Webhook signature verification failed:', error);
      return false;
    }
  }

  /**
   * Process file from webhook
   */
  private async processWebhookFile(file: any): Promise<Document | null> {
    // This would process files received via webhook
    // Implementation would depend on the webhook format
    console.warn('Webhook file processing not fully implemented');
    return null;
  }

  /**
   * Get processing statistics
   */
  getProcessingStats(): {
    supportedFormats: DocumentFormat[];
    enabledSources: DocumentSource[];
    maxFileSize: number;
    patternsCount: number;
  } {
    return {
      supportedFormats: this.autoIndexingConfig.supportedFormats,
      enabledSources: this.autoIndexingConfig.sources,
      maxFileSize: this.autoIndexingConfig.maxFileSize,
      patternsCount: this.autoIndexingConfig.patterns.length,
    };
  }
}

/**
 * Create auto document processor with default configuration
 */
export function createAutoDocumentProcessor(env: RAGEnv, config?: Partial<AutoIndexingConfig>): AutoDocumentProcessor {
  const defaultConfig: AutoIndexingConfig = {
    enabled: env.RAG_AUTO_INDEXING === 'true',
    sources: ['r2'],
    patterns: ['**/*'],
    excludePatterns: ['**/.*', '**/*.tmp', '**/*.log'],
    maxFileSize: 100 * 1024 * 1024, // 100MB
    supportedFormats: ['markdown', 'html', 'pdf', 'txt', 'docx', 'csv', 'json'],
    ...config,
  };

  return new AutoDocumentProcessor(env, defaultConfig);
}